import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import type { AgentThread, AgentThreadMessage, CompanyAgent } from '../../services/company-api.js';

@customElement('cp-agent-thread-panel')
export class CpAgentThreadPanel extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId!: number;
  @property({ type: Array }) agents: CompanyAgent[] = [];

  @state() private _threads: AgentThread[] = [];
  @state() private _activeThread: AgentThread | null = null;
  @state() private _messages: AgentThreadMessage[] = [];
  @state() private _loading = false;
  @state() private _loadingMessages = false;
  @state() private _error = '';

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadThreads();
  }

  override updated(changedProps: Map<string, unknown>): void {
    if (changedProps.has('companyId') && this.companyId) {
      void this._loadThreads();
    }
  }

  private async _loadThreads(): Promise<void> {
    if (!this.companyId) return;
    this._loading = true;
    this._error = '';
    try {
      this._threads = await companyApi.listAgentToAgentThreads(this.companyId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden der Agent-Kommunikation';
    } finally {
      this._loading = false;
    }
  }

  private async _selectThread(thread: AgentThread): Promise<void> {
    if (this._loadingMessages) return;
    this._activeThread = thread;
    this._messages = [];
    this._loadingMessages = true;
    try {
      const data = await companyApi.getAgentToAgentThread(thread.id);
      this._messages = data.messages;
      this._scrollToBottom();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden der Nachrichten';
    } finally {
      this._loadingMessages = false;
    }
  }

  private _scrollToBottom(): void {
    requestAnimationFrame(() => {
      const container = this.querySelector('.cp-thread-panel__messages');
      if (container) container.scrollTop = container.scrollHeight;
    });
  }

  private _getAgentName(agentId: number | null): string {
    if (agentId === null) return 'Unbekannt';
    const agent = this.agents.find(a => a.id === agentId);
    return agent?.name ?? `Agent #${agentId}`;
  }

  private _getAgentColor(agentId: number | null): string {
    if (agentId === null) return '#6b7280';
    const agent = this.agents.find(a => a.id === agentId);
    return agent?.color ?? '#6b7280';
  }

  private _formatTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  private _renderAgentAvatar(agentId: number | null, size = 28): unknown {
    const color = this._getAgentColor(agentId);
    const name = this._getAgentName(agentId);
    const initials = name.split(' ').filter(w => w.length > 0).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    return html`
      <div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;color:#fff;font-size:${Math.round(size * 0.4)}px;font-weight:600;flex-shrink:0;">
        ${initials}
      </div>
    `;
  }

  private _renderEmptyState(): unknown {
    return html`
      <div class="cp-thread-panel__empty">
        <div class="cp-thread-panel__empty-icon">🤖</div>
        <div class="cp-thread-panel__empty-title">Noch keine Agent-Kommunikation</div>
        <div class="cp-thread-panel__empty-desc">Agents kommunizieren automatisch wenn sie Aufgaben delegieren oder zusammenarbeiten.</div>
      </div>
    `;
  }

  private _renderThreadList(): unknown {
    if (this._loading) {
      return html`<div class="cp-thread-panel__loading">Lade Threads...</div>`;
    }
    if (this._threads.length === 0) {
      return this._renderEmptyState();
    }
    return html`
      <div class="cp-thread-panel__thread-list">
        ${this._threads.map(thread => this._renderThreadItem(thread))}
      </div>
    `;
  }

  private _renderThreadItem(thread: AgentThread): unknown {
    const isActive = this._activeThread?.id === thread.id;
    const fromName = this._getAgentName(thread.from_agent_id);
    const toName = this._getAgentName(thread.agent_id);
    return html`
      <div
        @click=${() => this._selectThread(thread)}
        class="cp-thread-panel__thread-item ${isActive ? 'cp-thread-panel__thread-item--active' : ''}"
      >
        <div class="cp-thread-panel__thread-avatars">
          ${this._renderAgentAvatar(thread.from_agent_id, 28)}
          <div class="cp-thread-panel__thread-avatar-secondary">
            ${this._renderAgentAvatar(thread.agent_id, 18)}
          </div>
        </div>
        <div class="cp-thread-panel__thread-info">
          <div class="cp-thread-panel__thread-topic">${thread.topic}</div>
          <div class="cp-thread-panel__thread-agents">${fromName} → ${toName}</div>
        </div>
        <div class="cp-thread-panel__thread-time">${this._formatTime(thread.updated_at)}</div>
      </div>
    `;
  }

  private _renderMessages(): unknown {
    if (!this._activeThread) {
      return html`
        <div class="cp-thread-panel__select-hint">
          Thread auswaehlen um Kommunikation zu lesen
        </div>
      `;
    }

    return html`
      <div style="display:flex;flex-direction:column;height:100%;">
        ${this._renderMessageHeader()}
        <div class="cp-thread-panel__messages">
          ${this._loadingMessages
            ? html`<div class="cp-thread-panel__msg-loading">Lade Nachrichten...</div>`
            : this._messages.length === 0
              ? html`<div class="cp-thread-panel__msg-loading">Noch keine Nachrichten in diesem Thread.</div>`
              : this._messages.map(msg => this._renderMessage(msg))
          }
        </div>
        ${this._renderReadOnlyBadge()}
      </div>
    `;
  }

  private _renderMessageHeader(): unknown {
    const thread = this._activeThread!;
    const fromName = this._getAgentName(thread.from_agent_id);
    const toName = this._getAgentName(thread.agent_id);
    return html`
      <div class="cp-thread-panel__msg-header">
        <div class="cp-thread-panel__thread-avatars">
          ${this._renderAgentAvatar(thread.from_agent_id, 32)}
          <div class="cp-thread-panel__thread-avatar-secondary">
            ${this._renderAgentAvatar(thread.agent_id, 20)}
          </div>
        </div>
        <div class="cp-thread-panel__msg-header-info">
          <div class="cp-thread-panel__msg-header-topic">${thread.topic}</div>
          <div class="cp-thread-panel__msg-header-agents">${fromName} → ${toName}</div>
        </div>
      </div>
    `;
  }

  private _renderMessage(message: AgentThreadMessage): unknown {
    const thread = this._activeThread!;
    const agentId = message.role === 'user' ? thread.from_agent_id : thread.agent_id;
    const agentName = this._getAgentName(agentId);
    const isFromAgent = message.role === 'user';

    return html`
      <div class="cp-thread-panel__msg ${isFromAgent ? '' : 'cp-thread-panel__msg--right'}">
        ${this._renderAgentAvatar(agentId, 28)}
        <div class="cp-thread-panel__msg-content ${isFromAgent ? '' : 'cp-thread-panel__msg-content--right'}">
          <div class="cp-thread-panel__msg-sender ${isFromAgent ? '' : 'cp-thread-panel__msg-sender--right'}">${agentName}</div>
          <div class="cp-thread-panel__msg-bubble ${isFromAgent ? 'cp-thread-panel__msg-bubble--left' : 'cp-thread-panel__msg-bubble--right'}">
            ${message.content}
          </div>
          <div class="cp-thread-panel__msg-time ${isFromAgent ? '' : 'cp-thread-panel__msg-time--right'}">${this._formatTime(message.created_at)}</div>
        </div>
      </div>
    `;
  }

  private _renderReadOnlyBadge(): unknown {
    return html`
      <div class="cp-thread-panel__readonly">
        <span class="cp-thread-panel__readonly-text">🔒</span>
        <span class="cp-thread-panel__readonly-text">Nur-Lesen — Gruender kann Agent-Kommunikation beobachten</span>
      </div>
    `;
  }

  override render(): unknown {
    return html`
      <div class="cp-thread-panel">
        <div class="cp-thread-panel__sidebar">
          <div class="cp-thread-panel__sidebar-header">
            <span class="cp-thread-panel__sidebar-title">Agent-Kommunikation</span>
            <span class="cp-thread-panel__sidebar-count">${this._threads.length} Threads</span>
          </div>
          ${this._renderThreadList()}
        </div>

        <div class="cp-thread-panel__main">
          ${this._error ? html`<div class="cp-thread-panel__error">${this._error}</div>` : nothing}
          ${this._renderMessages()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-thread-panel': CpAgentThreadPanel;
  }
}
